import React, { useEffect } from 'react';
import { ModalsKeys } from 'global';
import { useSelector } from 'react-redux';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { Send } from '@mui/icons-material';
import { blue } from '@mui/material/colors';
import { voSelectors } from 'redux/voReducer';
import makeStyles from '@mui/styles/makeStyles';
import { useProposal } from 'modules/proposals';
import { CustomDialog } from 'components/shared/Dialog';
import { Stack, Button, Box, Typography } from '@mui/material';
import { QuillInputField, RadioGroupField } from 'components/form';
import { NotificationSection } from 'components/segments/NotificationSection';
import { generateQuoteProposalPageURL } from 'utils/generateQuoteProposalPageURL';

import { quoteProposalMapper } from '../../../mappers';
import { CreateQuoteFormValues } from '../../../types';
import { sendMethodOptions } from '../../../../proposals/ui/dialogs/SentProposalDialog';

export const CreateAndSendQuoteModal = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.CreateAndSendQuote);

  const classes = useStyles();

  const dynamicsAccountId = useSelector<any, string>(state => state.account.dynamicsAccountId);
  const { all, main } = useSelector(voSelectors.selectRelatedUserEmails);

  const { control, setValue, getValues, handleSubmit } = useForm<CreateQuoteFormValues>({
    defaultValues: {
      notesToBuyer: '',
      notification: {
        emails: [],
        ccEmails: [],
        transport: 'email',
      },
    },
  });

  const { handleCreateQuoteProposal, isQuoteProposalCreating } = useProposal();

  const handleCreateAndSend = async (formData: CreateQuoteFormValues) => {
    const dto = quoteProposalMapper.processQuoteProposalFormToCreateDto(data.voId, formData);
    const proposalId = await handleCreateQuoteProposal(dto);

    if (proposalId) {
      const linkToProposalPage = generateQuoteProposalPageURL(proposalId, dynamicsAccountId);
      window.open(linkToProposalPage, '_blank');
      handleClose();
    }
  };

  useEffect(() => {
    const isNeedSetMainRecipient = !getValues('notification.emails')
      .map(({ value }) => value)
      .includes(main.value);

    if (isNeedSetMainRecipient) {
      setValue('notification.emails', [main]);
    }
  }, [main, setValue, getValues]);

  return (
    <CustomDialog
      maxWidth="md"
      isOpen={isOpen}
      label="Send Quote"
      onClose={handleClose}
      actions={
        <Stack gap="1rem" justifyContent="flex-end" direction="row" width="100%">
          <Button onClick={handleClose} disabled={isQuoteProposalCreating}>
            Cancel
          </Button>

          <Button
            endIcon={<Send />}
            variant="contained"
            loading={isQuoteProposalCreating}
            onClick={handleSubmit(handleCreateAndSend)}
          >
            Send Quote
          </Button>
        </Stack>
      }
    >
      <Stack gap="1rem">
        <Stack gap="0.5rem">
          <Box className={classes.sectionHeader}>
            <Typography>Send Via</Typography>
          </Box>

          <RadioGroupField row name="notification.transport" control={control} options={sendMethodOptions} />

          <NotificationSection<CreateQuoteFormValues>
            ccRecipients={all}
            mainRecipient={main}
            formConnection={{ control, name: 'notification.ccEmails' }}
          />
        </Stack>

        <Stack gap="0.5rem">
          <Box className={classes.sectionHeader}>
            <Typography>Note to Buyer</Typography>
          </Box>

          <QuillInputField
            control={control}
            setValue={setValue}
            name="notesToBuyer"
            getValues={getValues}
            placeholder="Add notes that will be visible to the buyer..."
          />
        </Stack>
      </Stack>
    </CustomDialog>
  );
};

const useStyles = makeStyles({
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '0.3rem',
    backgroundColor: blue[50],
    padding: '0.75rem 1.25rem',
  },
});
